import { useEffect, useMemo, useRef, useState } from "react";
import "@/App.css";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Phone,
  MessageCircle,
  Check,
  Shield,
  Zap,
  Droplets,
  Star,
  ArrowRight,
  Sun,
  Wind,
  IndianRupee,
  Sparkles,
  ShieldCheck,
  Truck,
  ChevronRight,
  GripVertical,
  Play,
  X,
  Menu,
} from "lucide-react";

/* ----------------------------- Constants ----------------------------- */
const WA_NUMBER = "918238378852";
const WA_BASE_MSG = "Hi, I want to order PanelShine solar cleaning brush";
const PHONE_TEL = "+918238378852";
const PHONE_DISPLAY = "+91 82383 78852";
const EMAIL = "panelshine.ltd@gmail.com";

const BRUSH_IMG_1 =
  "https://customer-assets.emergentagent.com/job_37e035fb-d510-4e9a-9d2d-ba5ce294bd51/artifacts/u1w02wae_brush%201.png";
const BRUSH_IMG_2 =
  "https://customer-assets.emergentagent.com/job_37e035fb-d510-4e9a-9d2d-ba5ce294bd51/artifacts/abzj96nt_brush%202.jpg";
const BRUSH_IMG_3 =
  "https://customer-assets.emergentagent.com/job_37e035fb-d510-4e9a-9d2d-ba5ce294bd51/artifacts/eqw4sl2m_brush%203.jpg";
const BEFORE_IMG =
  "https://customer-assets.emergentagent.com/job_37e035fb-d510-4e9a-9d2d-ba5ce294bd51/artifacts/ibyyap9z_before%20image.jpg";
const AFTER_IMG =
  "https://customer-assets.emergentagent.com/job_37e035fb-d510-4e9a-9d2d-ba5ce294bd51/artifacts/gb9ha0mi_after%20image.png";
const LIQUID_IMG =
  "https://customer-assets.emergentagent.com/job_clean-panels-fast/artifacts/2iolyj5x_liquied.png";

const SIZES = [
  { id: "8ft", label: "8 ft", actual: 2999, sale: 1999, blurb: "Small rooftops · 1–2 kW" },
  { id: "12ft", label: "12 ft", actual: 3299, sale: 2199, blurb: "Medium homes · 3–5 kW", popular: true },
  { id: "16ft", label: "16 ft", actual: 3699, sale: 2499, blurb: "Large systems · 6–10 kW+" },
];
const WATER_DISCOUNT = 300;

/* YouTube demo video */
const YT_ID = "g83u3av3lBI";
const YT_THUMB = `https://img.youtube.com/vi/${YT_ID}/maxresdefault.jpg`;
const YT_EMBED = `https://www.youtube.com/embed/${YT_ID}?autoplay=1&rel=0&modestbranding=1`;

/* ----------------------------- Helpers ----------------------------- */
const buildWA = (msg) =>
  `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(msg || WA_BASE_MSG)}`;
const inr = (n) => `₹${n.toLocaleString("en-IN")}`;

/* ============================================================
   LOGO — Solar panel + brush (product-defining)
   ============================================================ */
const Logo = ({ size = 36 }) => (
  <svg
    viewBox="0 0 48 48"
    width={size}
    height={size}
    aria-label="PanelShine logo"
    role="img"
  >
    <defs>
      <linearGradient id="psPanel" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#0A0A0A" />
        <stop offset="100%" stopColor="#1E293B" />
      </linearGradient>
      <linearGradient id="psBrush" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#FFD54F" />
        <stop offset="100%" stopColor="#FFC107" />
      </linearGradient>
    </defs>

    {/* Rounded badge background */}
    <rect x="1" y="1" width="46" height="46" rx="11" fill="#0A0A0A" />

    {/* Sun in top-right corner (above panel) */}
    <circle cx="35" cy="13" r="4" fill="#FFC107">
      <animate attributeName="r" values="3.6;4.4;3.6" dur="2.2s" repeatCount="indefinite" />
    </circle>
    {/* Sun rays */}
    {[0, 45, 90, 135, 180, 225, 270, 315].map((deg) => (
      <line
        key={deg}
        x1="35"
        y1="13"
        x2={35 + Math.cos((deg * Math.PI) / 180) * 7.5}
        y2={13 + Math.sin((deg * Math.PI) / 180) * 7.5}
        stroke="#FFC107"
        strokeWidth="1.2"
        strokeLinecap="round"
        opacity="0.85"
      />
    ))}

    {/* Solar panel (tilted rectangle with grid) */}
    <g transform="translate(6 20) rotate(-8)">
      <rect x="0" y="0" width="28" height="18" rx="1.5" fill="url(#psPanel)" stroke="#FFC107" strokeWidth="1" />
      {/* Grid lines */}
      <line x1="9.3" y1="1" x2="9.3" y2="17" stroke="#FFC107" strokeWidth="0.6" opacity="0.5" />
      <line x1="18.6" y1="1" x2="18.6" y2="17" stroke="#FFC107" strokeWidth="0.6" opacity="0.5" />
      <line x1="1" y1="9" x2="27" y2="9" stroke="#FFC107" strokeWidth="0.6" opacity="0.5" />
      {/* Shine highlight */}
      <rect x="1" y="1" width="26" height="3" rx="0.5" fill="#FFC107" opacity="0.25" />
    </g>

    {/* Brush (diagonal, top-right sweeping down-left across panel) */}
    <g transform="translate(40 6) rotate(35)">
      {/* Handle */}
      <rect x="-1" y="0" width="2" height="18" rx="1" fill="#E6A800" />
      {/* Brush head (yellow) */}
      <rect x="-5" y="17" width="10" height="4" rx="1" fill="url(#psBrush)" stroke="#0A0A0A" strokeWidth="0.8" />
      {/* Bristles */}
      {[-4, -2.5, -1, 0.5, 2, 3.5].map((x) => (
        <line key={x} x1={x} y1="21" x2={x} y2="25" stroke="#FFFFFF" strokeWidth="0.7" strokeLinecap="round" opacity="0.9" />
      ))}
    </g>
  </svg>
);

/* ============================================================
   Video Modal (YouTube lightbox)
   ============================================================ */
const VideoModal = ({ open, onClose }) => {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div
      className="video-modal"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      data-testid="video-modal"
    >
      <button
        className="video-close"
        onClick={onClose}
        aria-label="Close video"
        data-testid="video-close-btn"
      >
        <X className="w-6 h-6" />
      </button>
      <div className="video-frame" onClick={(e) => e.stopPropagation()}>
        <iframe
          src={YT_EMBED}
          title="PanelShine Solar Cleaning Brush Demo"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          data-testid="video-iframe"
        />
      </div>
    </div>
  );
};
const WhatsAppIcon = ({ className = "w-5 h-5" }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M20.52 3.48A11.78 11.78 0 0 0 12.06 0C5.5 0 .14 5.33.14 11.88a11.7 11.7 0 0 0 1.6 5.93L0 24l6.35-1.67a11.85 11.85 0 0 0 5.7 1.45h.01c6.56 0 11.91-5.33 11.91-11.88a11.78 11.78 0 0 0-3.45-8.42ZM12.06 21.8h-.01a9.86 9.86 0 0 1-5.03-1.38l-.36-.21-3.77.99 1.01-3.67-.24-.38a9.82 9.82 0 0 1-1.52-5.28c0-5.45 4.45-9.88 9.92-9.88a9.86 9.86 0 0 1 9.92 9.89c0 5.45-4.45 9.92-9.92 9.92Zm5.44-7.4c-.3-.15-1.77-.87-2.04-.97-.27-.1-.47-.15-.66.15-.2.3-.76.97-.93 1.17-.17.2-.34.22-.64.07-.3-.15-1.26-.46-2.4-1.48-.88-.79-1.48-1.77-1.65-2.07-.17-.3-.02-.46.13-.61.14-.14.3-.37.45-.55.15-.19.2-.3.3-.5.1-.2.05-.38-.02-.53-.07-.15-.66-1.59-.9-2.18-.24-.57-.48-.5-.66-.51l-.56-.01a1.08 1.08 0 0 0-.78.36c-.27.3-1.02.99-1.02 2.43s1.05 2.82 1.2 3.02c.15.2 2.06 3.14 5 4.4.7.3 1.24.47 1.67.61.7.22 1.33.19 1.83.12.56-.08 1.77-.72 2.02-1.42.25-.7.25-1.3.17-1.42-.07-.12-.27-.2-.57-.34Z"/>
  </svg>
);

const WABtn = ({ msg, label = "Order on WhatsApp", size = "md", className = "", testId }) => (
  <a
    href={buildWA(msg)}
    target="_blank"
    rel="noopener noreferrer"
    className={`btn-whatsapp ${size === "lg" ? "text-base px-8 py-4" : ""} ${className}`}
    data-testid={testId}
  >
    <WhatsAppIcon className="w-5 h-5" /> {label}
  </a>
);

const Eyebrow = ({ children, light }) => (
  <span className={`eyebrow ${light ? "eyebrow-light" : ""}`}>{children}</span>
);

const EnergyDivider = () => (
  <div className="energy-line" aria-hidden="true" />
);

/* Reveal on scroll */
const useReveal = () => {
  useEffect(() => {
    const els = document.querySelectorAll(".reveal");
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("in");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
};

/* Count-up hook */
const useCountUp = (target, duration = 1600, suffix = "", prefix = "") => {
  const ref = useRef(null);
  const [val, setVal] = useState(prefix + "0" + suffix);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            const start = performance.now();
            const tick = (t) => {
              const p = Math.min(1, (t - start) / duration);
              const eased = 1 - Math.pow(1 - p, 3);
              const n = Math.round(target * eased);
              setVal(prefix + n.toLocaleString("en-IN") + suffix);
              if (p < 1) requestAnimationFrame(tick);
            };
            requestAnimationFrame(tick);
            io.disconnect();
          }
        });
      },
      { threshold: 0.4 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [target, duration, suffix, prefix]);
  return [ref, val];
};

/* ============================================================
   Solar Sun — rotating rays + pulsing core (unique animation)
   ============================================================ */
const SolarSun = ({ className = "" }) => (
  <div className={`solar-sun ${className}`} aria-hidden="true">
    <div className="solar-sun-rays">
      {Array.from({ length: 12 }).map((_, i) => (
        <span key={i} style={{ transform: `rotate(${i * 30}deg)` }} />
      ))}
    </div>
    <div className="solar-sun-rays solar-sun-rays-alt">
      {Array.from({ length: 12 }).map((_, i) => (
        <span key={i} style={{ transform: `rotate(${i * 30 + 15}deg)` }} />
      ))}
    </div>
    <div className="solar-sun-core" />
  </div>
);

/* Charging solar-panel grid */
const ChargingGrid = ({ cols = 12, rows = 8, className = "" }) => {
  const cells = cols * rows;
  return (
    <div
      className={`solar-grid ${className}`}
      style={{ gridTemplateColumns: `repeat(${cols}, 1fr)`, gridTemplateRows: `repeat(${rows}, 1fr)` }}
      aria-hidden="true"
    >
      {Array.from({ length: cells }).map((_, i) => (
        <div
          key={i}
          className="solar-cell"
          style={{ animationDelay: `${((i * 97) % (cells * 50)) / 1000}s` }}
        />
      ))}
    </div>
  );
};

/* Floating dust particles */
const DustField = ({ count = 30 }) => (
  <div className="dust-field" aria-hidden="true">
    {Array.from({ length: count }).map((_, i) => {
      const size = 2 + Math.random() * 4;
      return (
        <span
          key={i}
          className="dust"
          style={{
            left: `${Math.random() * 100}%`,
            width: `${size}px`,
            height: `${size}px`,
            animationDuration: `${8 + Math.random() * 12}s`,
            animationDelay: `${Math.random() * 10}s`,
            opacity: 0.3 + Math.random() * 0.5,
          }}
        />
      );
    })}
  </div>
);

/* ============================================================
   Sections
   ============================================================ */
const OfferBar = () => (
  <div className="bg-black text-white text-sm overflow-hidden" data-testid="offer-bar">
    <div className="marquee-track py-2.5 font-medium">
      {Array.from({ length: 2 }).map((_, i) => (
        <div key={i} className="inline-flex items-center">
          <span className="inline-flex items-center gap-2"><Sparkles className="w-4 h-4 text-[color:var(--ps-yellow)]" /> Limited Time Offer — Save up to ₹1000</span>
          <span className="inline-flex items-center gap-2"><Zap className="w-4 h-4 text-[color:var(--ps-yellow)]" /> Free Replacement Guarantee</span>
          <span className="inline-flex items-center gap-2"><Truck className="w-4 h-4 text-[color:var(--ps-yellow)]" /> All India Shipping</span>
          <span className="inline-flex items-center gap-2"><Sun className="w-4 h-4 text-[color:var(--ps-yellow)]" /> Offer Ending Soon</span>
        </div>
      ))}
    </div>
  </div>
);

const Nav = () => {
  const [open, setOpen] = useState(false);
  const navLinks = [
    { href: "#product", label: "Product", id: "product" },
    { href: "#watch-demo", label: "Demo", id: "demo" },
    { href: "#compare", label: "Before/After", id: "compare" },
    { href: "#sizes", label: "Size Guide", id: "sizes" },
    { href: "#wholesale", label: "Wholesale", id: "wholesale", highlight: true },
    { href: "#faq", label: "FAQ", id: "faq" },
  ];
  return (
    <header className="sticky top-0 z-40 bg-white/90 backdrop-blur border-b border-black/5" data-testid="navbar">
      <div className="ps-container flex items-center justify-between py-3 gap-3">
        <a href="#top" className="flex items-center gap-2 flex-shrink-0" data-testid="logo-link">
          <Logo size={34} />
          <span className="font-display font-bold text-lg sm:text-xl tracking-tight">PanelShine</span>
        </a>
        <nav className="hidden lg:flex items-center gap-6 text-sm font-medium">
          {navLinks.map((l) => (
            <a
              key={l.id}
              href={l.href}
              className={`hover:text-black/70 ${l.highlight ? "text-[color:var(--ps-yellow-dark)] font-semibold" : ""}`}
              data-testid={`nav-${l.id}`}
            >
              {l.label}
            </a>
          ))}
        </nav>
        <div className="flex items-center gap-2">
          <a href={`tel:${PHONE_TEL}`} className="hidden sm:inline-flex btn-outline-dark !py-2 !px-3 md:!px-4 text-xs" data-testid="nav-call-btn">
            <Phone className="w-4 h-4" /> <span className="hidden md:inline">Call</span>
          </a>
          <WABtn label="Order" className="!py-2 !px-3 md:!px-4 text-xs" testId="nav-wa-btn" />
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden w-10 h-10 rounded-full border-2 border-black flex items-center justify-center"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            data-testid="nav-toggle-btn"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>
      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden border-t border-black/10 bg-white" data-testid="nav-drawer">
          <div className="ps-container py-4 grid gap-1">
            {navLinks.map((l) => (
              <a
                key={l.id}
                href={l.href}
                onClick={() => setOpen(false)}
                className={`block py-3 px-2 rounded-xl text-base font-semibold hover:bg-black/[0.04] ${l.highlight ? "text-[color:var(--ps-yellow-dark)]" : ""}`}
                data-testid={`mobile-nav-${l.id}`}
              >
                {l.label}
              </a>
            ))}
          </div>
        </div>
      )}
    </header>
  );
};

/* Stagger-reveal headline */
const StaggerHeadline = () => (
  <h1 className="font-display mt-5 text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.02] tracking-tight">
    <span className="stagger-word" style={{ animationDelay: "0.1s" }}>Best</span>{" "}
    <span className="stagger-word" style={{ animationDelay: "0.18s" }}>
      <span className="hl-underline">Solar</span>
    </span>{" "}
    <span className="stagger-word" style={{ animationDelay: "0.26s" }}>
      <span className="hl-underline">Panel</span>
    </span>{" "}
    <span className="stagger-word" style={{ animationDelay: "0.34s" }}>Cleaning</span>{" "}
    <span className="stagger-word" style={{ animationDelay: "0.42s" }}>Brush</span>
    <br />
    <span className="stagger-word" style={{ animationDelay: "0.55s" }}>in</span>{" "}
    <span className="stagger-word" style={{ animationDelay: "0.63s" }}>India.</span>
  </h1>
);

/* Animated circular efficiency gauge — SVG stroke-dashoffset */
const EfficiencyGauge = ({ target = 100 }) => {
  const [ref, val] = useCountUp(target, 2000, "%", "+");
  const circ = 2 * Math.PI * 46;
  const [offset, setOffset] = useState(circ);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setOffset(0);
            io.disconnect();
          }
        });
      },
      { threshold: 0.4 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [ref, circ]);
  return (
    <div ref={ref} className="relative w-20 h-20 sm:w-24 sm:h-24 md:w-28 md:h-28" aria-hidden="true">
      <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
        <circle cx="50" cy="50" r="46" fill="none" stroke="rgba(0,0,0,0.08)" strokeWidth="5" />
        <circle
          cx="50"
          cy="50"
          r="46"
          fill="none"
          stroke="url(#gaugeGrad)"
          strokeWidth="5"
          strokeLinecap="round"
          strokeDasharray={circ}
          strokeDashoffset={offset}
          style={{ transition: "stroke-dashoffset 2s cubic-bezier(0.2, 0.8, 0.2, 1)" }}
        />
        <defs>
          <linearGradient id="gaugeGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#FFC107" />
            <stop offset="100%" stopColor="#E6A800" />
          </linearGradient>
        </defs>
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div className="font-display font-bold text-lg sm:text-xl md:text-2xl leading-none">{val}</div>
        <div className="text-[8px] sm:text-[9px] md:text-[10px] tracking-widest uppercase text-black/50 mt-0.5 md:mt-1">Output</div>
      </div>
    </div>
  );
};

/* Premium ticker — rolling stats */
const HeroTicker = () => {
  const stats = [
    { k: "5,000+", v: "Indian homes" },
    { k: "4.8★", v: "1247 reviews" },
    { k: "30%", v: "Avg. efficiency boost" },
    { k: "24hr", v: "Pan-India dispatch" },
  ];
  return (
    <div className="mt-10 border-y border-black/10 py-5 grid grid-cols-2 md:grid-cols-4 gap-6">
      {stats.map((s, i) => (
        <div key={i} className="reveal" style={{ transitionDelay: `${i * 0.08}s` }}>
          <div className="font-display font-bold text-xl sm:text-2xl leading-none">{s.k}</div>
          <div className="text-[11px] tracking-widest uppercase text-black/50 mt-2">{s.v}</div>
        </div>
      ))}
    </div>
  );
};

const Hero = ({ onWatchDemo }) => {
  const imgRef = useRef(null);

  // Subtle parallax on mouse move
  useEffect(() => {
    const el = imgRef.current;
    if (!el) return;
    const onMove = (e) => {
      const rect = el.getBoundingClientRect();
      const x = (e.clientX - rect.left - rect.width / 2) / rect.width;
      const y = (e.clientY - rect.top - rect.height / 2) / rect.height;
      el.style.setProperty("--px", `${x * 12}px`);
      el.style.setProperty("--py", `${y * 12}px`);
      el.style.setProperty("--rx", `${-y * 4}deg`);
      el.style.setProperty("--ry", `${x * 6}deg`);
    };
    const onLeave = () => {
      el.style.setProperty("--px", "0px");
      el.style.setProperty("--py", "0px");
      el.style.setProperty("--rx", "0deg");
      el.style.setProperty("--ry", "0deg");
    };
    el.addEventListener("mousemove", onMove);
    el.addEventListener("mouseleave", onLeave);
    return () => {
      el.removeEventListener("mousemove", onMove);
      el.removeEventListener("mouseleave", onLeave);
    };
  }, []);

  return (
    <section id="top" className="relative overflow-hidden bg-white" data-testid="hero-section">
      {/* Premium aurora glow background */}
      <div className="aurora aurora-1" aria-hidden="true" />
      <div className="aurora aurora-2" aria-hidden="true" />
      <div className="hero-noise" aria-hidden="true" />

      <div className="ps-container pt-10 md:pt-16 pb-14 md:pb-24 grid md:grid-cols-12 gap-10 md:gap-14 items-center relative z-10">
        <div className="md:col-span-7 relative z-10">
          <div className="inline-flex items-center gap-2.5 bg-black text-white rounded-full pl-1.5 pr-3.5 py-1.5 text-xs font-semibold tracking-wide stagger-word" style={{ animationDelay: "0s" }}>
            <span className="inline-flex w-6 h-6 rounded-full bg-[color:var(--ps-yellow)] items-center justify-center">
              <Sun className="w-3.5 h-3.5 text-black" />
            </span>
            Trusted by 5,000+ Indian homes
          </div>

          <StaggerHeadline />

          <h2 className="mt-4 text-lg md:text-xl font-medium text-black/70 stagger-word max-w-xl" style={{ animationDelay: "0.7s" }}>
            Dirty solar panels = power loss. Restore up to 30% efficiency with a premium, water-fed, telescopic cleaning kit engineered for Indian rooftops.
          </h2>

          <div className="mt-7 flex flex-wrap gap-3 stagger-word" style={{ animationDelay: "0.85s" }}>
            <a href="#product" className="btn-primary" data-testid="hero-buy-btn">
              Buy Now <ArrowRight className="w-4 h-4" />
            </a>
            <WABtn label="WhatsApp Order" testId="hero-wa-btn" />
            <button
              type="button"
              onClick={onWatchDemo}
              className="btn-ghost-play"
              data-testid="hero-watch-demo-btn"
            >
              <span className="play-icon">
                <Play className="w-3.5 h-3.5 fill-current" />
              </span>
              Watch Demo
            </button>
          </div>

          <HeroTicker />
        </div>

        {/* Premium product visual */}
        <div className="md:col-span-5 relative perspective">
          <div ref={imgRef} className="hero-stage" data-testid="hero-visual">
            {/* Soft radial glow */}
            <div className="hero-glow" />

            {/* Main product image */}
            <div className="hero-product">
              <img
                src={AFTER_IMG}
                alt="PanelShine water-fed telescopic solar panel cleaning brush on Indian rooftop"
                className="w-full h-full object-cover rounded-3xl"
                loading="eager"
                data-testid="hero-image"
              />
              {/* Scanning line overlay */}
              <div className="scan-line" aria-hidden="true" />
            </div>

            {/* Floating gauge card */}
            <div className="hero-card hero-card-gauge">
              <EfficiencyGauge target={100} />
            </div>

            {/* Floating price card */}
            <div className="hero-card hero-card-price">
              <div className="text-[9px] tracking-[0.18em] font-bold text-black/50 uppercase">Starting</div>
              <div className="font-display text-3xl font-bold leading-none mt-1">₹1,999</div>
              <div className="text-[10px] text-black/40 line-through mt-0.5">₹2,999</div>
              <div className="mt-2 text-[10px] text-black/60 flex items-center gap-1">
                <span className="pulse-dot" /> In stock · ships 24h
              </div>
            </div>

            {/* Floating efficiency pill */}
            <div className="hero-card hero-card-boost">
              <div className="w-8 h-8 rounded-full bg-black flex items-center justify-center">
                <Zap className="w-4 h-4 text-[color:var(--ps-yellow)]" />
              </div>
              <div>
                <div className="text-[10px] text-black/50 tracking-widest uppercase">Efficiency</div>
                <div className="font-display font-bold text-lg leading-none">+30%</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Premium scroll indicator */}
      <div className="scroll-indicator" aria-hidden="true">
        <span>Scroll</span>
        <div className="scroll-line" />
      </div>

      <EnergyDivider />
    </section>
  );
};

/* Stat with count-up */
const AnimatedStat = ({ value, suffix = "", prefix = "", label, icon }) => {
  const [ref, val] = useCountUp(value, 1800, suffix, prefix);
  return (
    <div className="border border-white/10 rounded-2xl p-6 hover:border-[color:var(--ps-yellow)] transition-colors reveal">
      <div className="w-10 h-10 rounded-xl bg-[color:var(--ps-yellow)] text-black flex items-center justify-center">
        {icon}
      </div>
      <div ref={ref} className="font-display stat-value text-5xl mt-5">{val}</div>
      <div className="text-white/60 text-sm mt-1">{label}</div>
    </div>
  );
};

const Problem = () => (
  <section className="bg-[color:var(--ps-black)] text-white py-20 md:py-28 relative grain overflow-hidden" data-testid="problem-section">
    <DustField count={35} />
    <div className="ps-container grid md:grid-cols-12 gap-10 items-center relative z-10">
      <div className="md:col-span-5 reveal">
        <Eyebrow light>The Problem</Eyebrow>
        <h2 className="font-display text-4xl sm:text-5xl font-bold mt-4 leading-tight">
          Why Solar Panels Lose Efficiency in India
        </h2>
        <p className="mt-4 text-white/70 max-w-md">
          Indian rooftops face extreme dust, pollution and bird droppings. Most homeowners lose thousands in electricity savings — without even knowing it.
        </p>
      </div>
      <div className="md:col-span-7 grid sm:grid-cols-3 gap-4" data-testid="problem-stats">
        <AnimatedStat value={30} suffix="%" label="Efficiency loss from dust" icon={<Wind className="w-5 h-5" />} />
        <AnimatedStat value={20} prefix="₹" suffix="k+" label="Lost savings per year" icon={<IndianRupee className="w-5 h-5" />} />
        <AnimatedStat value={15} suffix="d" label="Panels get dirty in" icon={<Sun className="w-5 h-5" />} />
      </div>
    </div>
    <EnergyDivider />
  </section>
);

const Solution = () => (
  <section className="py-20 md:py-28 relative" data-testid="solution-section">
    <div className="ps-container grid md:grid-cols-2 gap-12 items-center">
      <div className="reveal order-2 md:order-1">
        <img
          src={BRUSH_IMG_1}
          alt="PanelShine telescopic water-fed brush cleaning solar panels — safe DIY cleaning"
          className="rounded-3xl w-full aspect-[4/5] object-cover"
          loading="lazy"
          data-testid="solution-image"
        />
      </div>
      <div className="reveal order-1 md:order-2">
        <Eyebrow>The Solution</Eyebrow>
        <h2 className="font-display text-4xl sm:text-5xl font-bold mt-4 leading-tight">
          A safer, smarter way<br /> to clean solar panels
        </h2>
        <p className="mt-4 text-black/60 text-base max-w-md">
          PanelShine gives you a professional-grade cleaning system for your rooftop — without climbing a ladder or hiring a service.
        </p>
        <ul className="mt-8 space-y-4">
          {[
            { t: "No ladder required", d: "Telescopic reach up to 16ft cleans from the ground safely." },
            { t: "Easy DIY cleaning", d: "Soft nylon bristles + water-fed system. Attach, extend, clean." },
            { t: "One-time investment", d: "Pays for itself in the first month with restored solar output." },
          ].map((i, k) => (
            <li key={k} className="flex gap-4" data-testid={`solution-point-${k}`}>
              <span className="w-10 h-10 flex-shrink-0 rounded-full bg-[color:var(--ps-yellow)] text-black flex items-center justify-center font-bold">
                <Check className="w-5 h-5" strokeWidth={3} />
              </span>
              <div>
                <div className="font-display font-bold text-lg">{i.t}</div>
                <div className="text-black/60 text-sm">{i.d}</div>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
    <EnergyDivider />
  </section>
);

const ProductMain = () => {
  const [sizeId, setSizeId] = useState("12ft");
  const [withWater, setWithWater] = useState(true);
  const [flipKey, setFlipKey] = useState(0);
  const size = useMemo(() => SIZES.find((s) => s.id === sizeId), [sizeId]);
  const sale = size.sale - (withWater ? 0 : WATER_DISCOUNT);
  const actual = size.actual - (withWater ? 0 : WATER_DISCOUNT);
  const save = actual - sale;

  // Trigger flip on price change
  useEffect(() => {
    setFlipKey((k) => k + 1);
  }, [sizeId, withWater]);

  const msg = `Hi, I want to order PanelShine solar cleaning brush — ${size.label}${withWater ? " with Water Flow System" : " without Water Flow"}. Price: ${inr(sale)}`;

  return (
    <section id="product" className="py-20 md:py-28 bg-[color:var(--ps-gray-50)] relative" data-testid="product-main-section">
      <div className="ps-container">
        <div className="grid md:grid-cols-2 gap-12 items-start">
          <div className="reveal">
            <div className="grid grid-cols-5 gap-3">
              <div className="col-span-5 aspect-square rounded-3xl overflow-hidden bg-white border border-black/5 relative">
                <img src={BRUSH_IMG_1} alt="PanelShine cleaning brush in action on solar panels" className="w-full h-full object-cover" loading="lazy" />
                <div className="absolute bottom-4 right-4 bg-black text-white text-[10px] tracking-widest uppercase font-bold px-2.5 py-1.5 rounded-full flex items-center gap-1.5 shadow-lg">
                  <span className="pulse-dot" /> In Stock
                </div>
              </div>
              <div className="col-span-2 aspect-[4/3] rounded-2xl overflow-hidden bg-white border border-black/5">
                <img src={BRUSH_IMG_2} alt="Ultra-soft nylon bristle close-up 18 inch" className="w-full h-full object-cover" loading="lazy" />
              </div>
              <div className="col-span-3 aspect-[4/3] rounded-2xl overflow-hidden bg-white border border-black/5">
                <img src={BRUSH_IMG_3} alt="Telescopic pole and water-fed system" className="w-full h-full object-cover" loading="lazy" />
              </div>
            </div>
          </div>

          <div className="reveal md:sticky md:top-24">
            <Eyebrow>Product 01 · Best Seller</Eyebrow>
            <h2 className="font-display text-3xl sm:text-4xl font-bold mt-3 leading-tight">
              PanelShine Telescopic Solar Cleaning Brush
            </h2>
            <div className="flex items-center gap-2 mt-3">
              <div className="flex items-center gap-0.5 text-[color:var(--ps-yellow-dark)]">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-current" />
                ))}
              </div>
              <span className="text-sm text-black/60">4.8 · 1247 verified reviews</span>
            </div>

            <div className="mt-6 flex items-end gap-3">
              <div
                key={flipKey}
                className="font-display text-5xl font-bold leading-none price-flip"
                data-testid="product-price-sale"
              >
                {inr(sale)}
              </div>
              <div className="text-black/40 line-through text-lg" data-testid="product-price-actual">{inr(actual)}</div>
              <div className="bg-black text-white text-xs font-bold px-2.5 py-1 rounded-full">
                Save {inr(save)}
              </div>
            </div>
            <div className="text-xs text-black/50 mt-1">Inclusive of all taxes · Free All-India shipping</div>

            <div className="mt-7">
              <div className="flex items-center justify-between mb-3">
                <div className="text-sm font-bold tracking-wide uppercase">Choose Size</div>
                <a href="#sizes" className="text-xs underline underline-offset-4 font-semibold">Size guide</a>
              </div>
              <div className="grid grid-cols-3 gap-2 sm:gap-3">
                {SIZES.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => setSizeId(s.id)}
                    className={`size-pill text-left ${sizeId === s.id ? "active" : ""}`}
                    data-testid={`size-option-${s.id}`}
                  >
                    <div className="flex items-center justify-between gap-1">
                      <div className="font-display font-bold text-base sm:text-lg leading-none">{s.label}</div>
                      {s.popular && (
                        <span className="text-[8px] sm:text-[9px] font-bold bg-[color:var(--ps-yellow)] text-black px-1 py-0.5 rounded-full leading-none">POPULAR</span>
                      )}
                    </div>
                    <div className={`text-[10px] sm:text-[11px] mt-1.5 ${sizeId === s.id ? "text-white/70" : "text-black/50"}`}>
                      {s.blurb}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <label className="mt-6 flex items-start gap-3 border-2 border-black rounded-2xl p-4 cursor-pointer hover:bg-black/[0.03] transition" data-testid="water-flow-toggle">
              <Checkbox
                checked={withWater}
                onCheckedChange={(v) => setWithWater(Boolean(v))}
                className="mt-0.5 border-2 border-black data-[state=checked]:bg-[color:var(--ps-yellow)] data-[state=checked]:text-black data-[state=checked]:border-black"
                data-testid="water-flow-checkbox"
              />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <Droplets className="w-4 h-4" />
                  <span className="font-display font-bold">With Water Flow System</span>
                  <span className="text-[10px] font-bold bg-[color:var(--ps-yellow)] text-black px-1.5 py-0.5 rounded-full">RECOMMENDED</span>
                </div>
                <div className="text-xs text-black/60 mt-1">
                  Integrated water pipe + valve for bird droppings & stubborn grime. Uncheck to save {inr(WATER_DISCOUNT)}.
                </div>
              </div>
            </label>

            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <WABtn msg={msg} label="WhatsApp Order" size="lg" testId="product-wa-buy" />
              <a href={`tel:${PHONE_TEL}`} className="btn-outline-dark" data-testid="product-call-btn">
                <Phone className="w-4 h-4" /> Call Now
              </a>
            </div>

            <div className="mt-5 grid grid-cols-3 gap-3 text-[11px] font-semibold">
              <div className="flex items-center gap-1.5"><ShieldCheck className="w-4 h-4" /> Free Replacement</div>
              <div className="flex items-center gap-1.5"><Truck className="w-4 h-4" /> All India Shipping</div>
              <div className="flex items-center gap-1.5"><Sparkles className="w-4 h-4" /> Premium Build</div>
            </div>
          </div>
        </div>
      </div>
      <EnergyDivider />
    </section>
  );
};

const ProductLiquid = () => (
  <section className="py-20 md:py-28 bg-white relative" data-testid="product-liquid-section">
    <div className="ps-container grid md:grid-cols-2 gap-12 items-center">
      <div className="reveal order-2 md:order-1">
        <Eyebrow>Product 02 · Pair With Brush</Eyebrow>
        <h2 className="font-display text-3xl sm:text-4xl font-bold mt-3 leading-tight">
          PanelShine Eco-Friendly Cleaning Liquid
        </h2>
        <p className="mt-3 text-black/60 max-w-md">
          A professional-grade concentrated formula that dissolves oil, dust and bird droppings — gentle on the anti-reflective coating of your panels.
        </p>
        <ul className="mt-6 space-y-3 text-sm">
          {[
            "Concentration Formula — 1 L cleans 150+ panels",
            "No side effect on panels (anti-reflective safe)",
            "Eco-friendly & biodegradable",
            "Removes oil, dust, bird droppings & pollutants",
          ].map((t, i) => (
            <li key={i} className="flex items-center gap-3" data-testid={`liquid-feature-${i}`}>
              <span className="w-5 h-5 rounded-full bg-[color:var(--ps-yellow)] flex items-center justify-center flex-shrink-0">
                <Check className="w-3 h-3" strokeWidth={3} />
              </span>
              {t}
            </li>
          ))}
        </ul>

        <div className="mt-7 flex items-end gap-3">
          <div className="font-display text-4xl font-bold leading-none">₹600</div>
          <div className="text-black/40 line-through">₹799</div>
          <div className="bg-black text-white text-xs font-bold px-2.5 py-1 rounded-full">Save ₹199</div>
        </div>

        <div className="mt-5 flex flex-wrap gap-3">
          <WABtn
            msg="Hi, I want to order PanelShine Eco-Friendly Cleaning Liquid (₹600)"
            label="Order Liquid on WhatsApp"
            testId="liquid-wa-btn"
          />
        </div>
      </div>

      <div className="reveal order-1 md:order-2">
        <div className="relative aspect-[4/5] rounded-3xl overflow-hidden bg-gradient-to-br from-slate-900 via-black to-slate-900">
          {/* Soft glow */}
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_55%,rgba(255,193,7,0.18),transparent_60%)]" />
          {/* Water splash glows */}
          <div className="absolute -top-10 -left-10 w-48 h-48 rounded-full bg-[color:var(--ps-yellow)]/10 blur-3xl" />
          <div className="absolute bottom-0 right-0 w-56 h-56 rounded-full bg-sky-400/15 blur-3xl" />

          <img
            src={LIQUID_IMG}
            alt="PanelShine Solar Panel Cleaner liquid bottle with water splash"
            className="absolute inset-0 w-full h-full object-cover"
            loading="lazy"
            data-testid="liquid-image"
          />

          {/* Floating tag */}
          <div className="absolute top-5 left-5 bg-white/95 backdrop-blur rounded-full px-3 py-1.5 text-[10px] tracking-[0.18em] uppercase font-bold flex items-center gap-1.5">
            <span className="pulse-dot" /> New Arrival
          </div>
          <div className="absolute bottom-5 left-5 right-5 bg-white/95 backdrop-blur rounded-2xl px-4 py-3 flex items-center justify-between">
            <div>
              <div className="text-[10px] tracking-widest uppercase text-black/50">Concentration</div>
              <div className="font-display font-bold text-sm mt-0.5">1 L cleans 150+ panels</div>
            </div>
            <div className="font-display font-bold text-xl">₹600</div>
          </div>
        </div>
      </div>
    </div>
    <EnergyDivider />
  </section>
);

/* Before/After — drag slider */
const BeforeAfterSlider = () => {
  const ref = useRef(null);
  const [pct, setPct] = useState(50);
  const dragging = useRef(false);

  const handleMove = (clientX) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const p = ((clientX - rect.left) / rect.width) * 100;
    setPct(Math.max(0, Math.min(100, p)));
  };

  const onPointerDown = (e) => {
    dragging.current = true;
    handleMove(e.clientX);
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e) => { if (dragging.current) handleMove(e.clientX); };
  const onPointerUp = () => { dragging.current = false; };

  return (
    <div
      ref={ref}
      className="ba-slider aspect-[16/10] md:aspect-[16/9]"
      style={{ "--ba": `${pct}%` }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerLeave={onPointerUp}
      data-testid="ba-slider"
    >
      {/* BEFORE (bottom layer — shows on left when handle is centered? We reverse: AFTER is the overlay */}
      <img src={BEFORE_IMG} alt="Dirty solar panel — 30% power loss before cleaning" loading="lazy" />
      <div className="ba-after-layer">
        <img src={AFTER_IMG} alt="Clean solar panel after PanelShine cleaning — 100% performance restored" loading="lazy" />
      </div>

      {/* Labels */}
      <span className="ba-label bg-red-600 text-white left-4">BEFORE · 30% Loss</span>
      <span className="ba-label bg-[color:var(--ps-yellow)] text-black right-4">AFTER · 100%</span>

      {/* Handle */}
      <div className="ba-handle" aria-label="Drag to reveal">
        <span className="ba-handle-icon">
          <GripVertical className="w-5 h-5" strokeWidth={3} />
        </span>
      </div>

      {/* Hint */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/80 text-white text-xs font-semibold px-3 py-1.5 rounded-full backdrop-blur flex items-center gap-2 pointer-events-none">
        <span>← Drag to see the difference →</span>
      </div>
    </div>
  );
};

const Compare = () => (
  <section id="compare" className="py-20 md:py-28 bg-[color:var(--ps-gray-50)] relative" data-testid="compare-section">
    <div className="ps-container">
      <div className="max-w-2xl reveal">
        <Eyebrow>The Difference</Eyebrow>
        <h2 className="font-display text-4xl sm:text-5xl font-bold mt-4 leading-tight">
          Before PanelShine<br /> vs After PanelShine
        </h2>
        <p className="mt-4 text-black/60">
          The same rooftop, the same panels. Just a 10-minute clean. <span className="font-semibold text-black">Drag the handle to compare.</span>
        </p>
      </div>

      <div className="mt-12 reveal">
        <BeforeAfterSlider />
      </div>

      <div className="mt-10 flex flex-wrap gap-3 reveal">
        <WABtn label="Order Now & Save 30% Power" testId="compare-wa-btn" />
        <a href="#product" className="btn-dark" data-testid="compare-buy-btn">Shop Brush <ArrowRight className="w-4 h-4" /></a>
      </div>
    </div>
    <EnergyDivider />
  </section>
);

const SizeGuide = () => (
  <section id="sizes" className="py-20 md:py-28 relative" data-testid="size-guide-section">
    <div className="ps-container">
      <div className="max-w-3xl reveal">
        <Eyebrow>Size Guide</Eyebrow>
        <h2 className="font-display text-4xl sm:text-5xl font-bold mt-4 leading-tight">
          Which Solar Cleaning Brush Size is Best?
        </h2>
        <p className="mt-4 text-black/60">
          Pick the right length based on your rooftop size and solar capacity (kW).
        </p>
      </div>

      <div className="mt-12 grid md:grid-cols-3 gap-5">
        {SIZES.map((s, i) => (
          <div
            key={s.id}
            className={`ps-card p-7 relative reveal ${s.popular ? "border-2 border-black" : ""}`}
            data-testid={`size-card-${s.id}`}
          >
            {s.popular && (
              <span className="absolute -top-3 left-6 bg-[color:var(--ps-yellow)] text-black text-xs font-bold px-3 py-1 rounded-full">
                ⭐ MOST POPULAR
              </span>
            )}
            <div className="num-outline">0{i + 1}</div>
            <div className="mt-5 font-display text-3xl font-bold">{s.label} Brush</div>
            <div className="text-black/60 text-sm mt-1">{s.blurb}</div>
            <div className="mt-6 flex items-end gap-2">
              <div className="font-display text-2xl font-bold">{inr(s.sale)}</div>
              <div className="text-black/40 line-through text-sm">{inr(s.actual)}</div>
            </div>
            <WABtn
              msg={`Hi, I want to order PanelShine ${s.label} Solar Cleaning Brush (${inr(s.sale)})`}
              label={`Order ${s.label}`}
              className="mt-5 w-full"
              testId={`size-wa-${s.id}`}
            />
          </div>
        ))}
      </div>
    </div>
    <EnergyDivider />
  </section>
);

const Benefits = () => {
  const items = [
    { n: "01", t: "Instant output boost", d: "Recover up to 30% lost solar output after one cleaning session.", icon: <Zap className="w-5 h-5" /> },
    { n: "02", t: "₹10,000–₹20,000 saved/yr", d: "Stop throwing away money on lost kWh. The kit pays for itself.", icon: <IndianRupee className="w-5 h-5" /> },
    { n: "03", t: "No ladder required", d: "Clean safely from the ground with the telescopic pole.", icon: <Shield className="w-5 h-5" /> },
    { n: "04", t: "Panel-safe soft bristles", d: "Ultra-soft nylon bristles won't scratch the anti-reflective coating.", icon: <Sparkles className="w-5 h-5" /> },
  ];
  return (
    <section className="py-20 md:py-28 bg-[color:var(--ps-black)] text-white relative overflow-hidden" data-testid="benefits-section">
      <DustField count={20} />
      <div className="ps-container relative z-10">
        <div className="max-w-2xl reveal">
          <Eyebrow light>Benefits</Eyebrow>
          <h2 className="font-display text-4xl sm:text-5xl font-bold mt-4 leading-tight">
            Benefits of Solar<br /> Panel Cleaning
          </h2>
        </div>
        <div className="mt-12 grid md:grid-cols-4 gap-5">
          {items.map((i, k) => (
            <div key={k} className="border border-white/10 rounded-2xl p-6 hover:border-[color:var(--ps-yellow)] hover:bg-white/5 transition-colors reveal" data-testid={`benefit-${k}`}>
              <div className="flex items-center justify-between">
                <div className="w-10 h-10 rounded-xl bg-[color:var(--ps-yellow)] text-black flex items-center justify-center">
                  {i.icon}
                </div>
                <span className="text-white/40 text-xs font-mono">{i.n}</span>
              </div>
              <div className="font-display font-bold text-xl mt-5">{i.t}</div>
              <div className="text-white/60 text-sm mt-2">{i.d}</div>
            </div>
          ))}
        </div>
      </div>
      <EnergyDivider />
    </section>
  );
};

const HowItWorks = () => {
  const steps = [
    { t: "Attach water pipe", d: "Connect any standard garden hose to the built-in quick valve. Optional for dry cleaning.", icon: <Droplets className="w-5 h-5" /> },
    { t: "Extend the brush", d: "Telescopic aluminium pole extends up to 16 ft. Lock at any length.", icon: <ArrowRight className="w-5 h-5" /> },
    { t: "Clean panels", d: "Glide the soft nylon brush across panels. Dust, droppings and grime lift off instantly.", icon: <Sparkles className="w-5 h-5" /> },
  ];
  return (
    <section className="py-20 md:py-28 relative" data-testid="how-it-works-section">
      <div className="ps-container">
        <div className="max-w-2xl reveal">
          <Eyebrow>How it Works</Eyebrow>
          <h2 className="font-display text-4xl sm:text-5xl font-bold mt-4 leading-tight">
            How to Use Solar Panel Cleaning Brush
          </h2>
          <p className="mt-4 text-black/60">Three steps. Ten minutes. Full efficiency restored.</p>
        </div>

        <div className="mt-14 grid md:grid-cols-3 gap-8 relative">
          {steps.map((s, i) => (
            <div key={i} className="relative reveal" data-testid={`step-${i + 1}`}>
              <div className="num-outline">0{i + 1}</div>
              <div className="mt-4 w-12 h-12 rounded-full bg-[color:var(--ps-yellow)] flex items-center justify-center float-y" style={{ animationDelay: `${i * 0.3}s` }}>
                {s.icon}
              </div>
              <div className="font-display font-bold text-2xl mt-4">{s.t}</div>
              <div className="text-black/60 text-sm mt-2 max-w-xs">{s.d}</div>
            </div>
          ))}
        </div>
      </div>
      <EnergyDivider />
    </section>
  );
};

const Testimonials = () => {
  const list = [
    { name: "Rajesh Kumar", city: "Mumbai", text: "My 5kW system was giving 18 units a day after monsoon dust. After using PanelShine, it's back to 26 units. ROI in 2 months.", size: "12ft Brush" },
    { name: "Priya Sharma", city: "Delhi", text: "Pollution in Delhi is brutal. This brush is a lifesaver — I clean every 2 weeks and my bill dropped ₹1800/month.", size: "12ft Brush" },
    { name: "Arjun Patel", city: "Bangalore", text: "Solid build quality. The water-fed system really helps with bird droppings. Wish I had bought this a year ago.", size: "16ft Brush" },
  ];
  return (
    <section className="py-20 md:py-28 bg-[color:var(--ps-gray-50)] relative" data-testid="testimonials-section">
      <div className="ps-container">
        <div className="flex flex-wrap items-end justify-between gap-6 reveal">
          <div className="max-w-xl">
            <Eyebrow>Customer Stories</Eyebrow>
            <h2 className="font-display text-4xl sm:text-5xl font-bold mt-4 leading-tight">
              Trusted by Indian homes across the country
            </h2>
          </div>
          <div className="flex items-center gap-2 text-sm font-semibold">
            <div className="flex items-center gap-0.5 text-[color:var(--ps-yellow-dark)]">
              {Array.from({ length: 5 }).map((_, i) => <Star key={i} className="w-4 h-4 fill-current" />)}
            </div>
            4.8 / 5 · 1247 reviews
          </div>
        </div>

        <div className="mt-10 grid md:grid-cols-3 gap-5">
          {list.map((t, i) => (
            <div key={i} className="ps-card p-7 reveal" data-testid={`testimonial-${i}`}>
              <div className="flex items-center gap-0.5 text-[color:var(--ps-yellow-dark)]">
                {Array.from({ length: 5 }).map((_, j) => <Star key={j} className="w-4 h-4 fill-current" />)}
              </div>
              <p className="mt-4 text-[15px] leading-relaxed text-black/80">"{t.text}"</p>
              <div className="mt-6 pt-5 border-t border-black/10 flex items-center justify-between">
                <div>
                  <div className="font-display font-bold">{t.name}</div>
                  <div className="text-xs text-black/50">{t.city}, India</div>
                </div>
                <div className="text-[10px] uppercase tracking-widest font-bold bg-black text-white px-2 py-1 rounded-full">
                  {t.size}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <EnergyDivider />
    </section>
  );
};

const Policy = () => (
  <section className="py-20 md:py-28 relative" data-testid="policy-section">
    <div className="ps-container grid md:grid-cols-2 gap-12 items-center">
      <div className="reveal">
        <Eyebrow>Our Policy</Eyebrow>
        <h2 className="font-display text-4xl sm:text-5xl font-bold mt-4 leading-tight">
          Free Replacement Guarantee
        </h2>
        <p className="mt-4 text-black/60 max-w-md">
          We stand behind the build quality of every PanelShine kit that leaves our warehouse.
        </p>

        <div className="mt-8 border-2 border-black rounded-2xl p-6">
          <div className="flex items-start gap-3">
            <ShieldCheck className="w-6 h-6 flex-shrink-0" />
            <div>
              <div className="font-display font-bold text-lg">Free replacement on defects</div>
              <div className="text-sm text-black/60 mt-1">If your product arrives defective, we replace it at no cost.</div>
            </div>
          </div>
          <div className="mt-5 pt-5 border-t border-black/10 flex items-start gap-3 bg-[color:var(--ps-yellow)]/20 -m-6 mt-5 p-6 rounded-b-xl">
            <Sparkles className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <span className="font-bold">Condition:</span> Customer must record a clear, unedited unboxing video at the time of delivery to claim a free replacement.
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 reveal">
        {[
          { t: "Free Replacement", d: "Defect claims", icon: <ShieldCheck className="w-5 h-5" /> },
          { t: "All-India Shipping", d: "Dispatched in 24h", icon: <Truck className="w-5 h-5" /> },
          { t: "Premium Build", d: "Aluminium + nylon", icon: <Sparkles className="w-5 h-5" /> },
          { t: "WhatsApp Support", d: "7 days a week", icon: <MessageCircle className="w-5 h-5" /> },
        ].map((b, i) => (
          <div key={i} className="ps-card p-6" data-testid={`policy-badge-${i}`}>
            <div className="w-10 h-10 rounded-xl bg-black text-[color:var(--ps-yellow)] flex items-center justify-center">
              {b.icon}
            </div>
            <div className="font-display font-bold mt-4">{b.t}</div>
            <div className="text-xs text-black/60 mt-1">{b.d}</div>
          </div>
        ))}
      </div>
    </div>
    <EnergyDivider />
  </section>
);

const FAQSection = () => {
  const faqs = [
    { q: "How often should I clean solar panels?", a: "In Indian conditions with dust, pollution and birds, clean your panels every 15–30 days for peak output. Monsoon and dust-storm seasons may require weekly cleaning." },
    { q: "Can I clean panels without water?", a: "Yes — PanelShine works dry for light dust. For bird droppings, oil and stubborn grime, use it with water and our eco-friendly PanelShine cleaning liquid for best results." },
    { q: "Is it safe for solar panels?", a: "Absolutely. Our ultra-soft nylon bristles are engineered not to scratch or damage the anti-reflective coating on solar panels." },
    { q: "Does it really increase efficiency?", a: "Yes. Independent studies and customer data show regular cleaning restores 20–30% efficiency lost to dust and grime — saving thousands each year." },
    { q: "Which size should I order?", a: "8 ft for small rooftops (1–2 kW), 12 ft for typical Indian homes (3–5 kW — our bestseller), 16 ft for large commercial or industrial systems (6–10 kW+)." },
    { q: "Do you ship across India?", a: "Yes, we ship pan-India with free shipping. Most orders are dispatched within 24 hours." },
  ];
  return (
    <section id="faq" className="py-20 md:py-28 bg-[color:var(--ps-gray-50)] relative" data-testid="faq-section">
      <div className="ps-container grid md:grid-cols-12 gap-12">
        <div className="md:col-span-5 reveal">
          <Eyebrow>FAQ</Eyebrow>
          <h2 className="font-display text-4xl sm:text-5xl font-bold mt-4 leading-tight">
            Frequently Asked Questions
          </h2>
          <p className="mt-4 text-black/60 max-w-sm">Still unsure? Message us on WhatsApp — we reply in minutes.</p>
          <WABtn label="Ask on WhatsApp" className="mt-6" testId="faq-wa-btn" />
        </div>
        <div className="md:col-span-7 reveal">
          <Accordion type="single" collapsible className="space-y-3">
            {faqs.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="faq-item border border-black/10 rounded-2xl px-5 bg-white" data-testid={`faq-item-${i}`}>
                <AccordionTrigger className="font-display font-bold text-left hover:no-underline py-5">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-black/70 pb-5 text-[15px] leading-relaxed">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
      <EnergyDivider />
    </section>
  );
};

const FinalCTA = () => (
  <section className="py-20 md:py-28 relative overflow-hidden bg-[color:var(--ps-yellow)]" data-testid="final-cta-section">
    <div className="absolute inset-0 grain" />
    {/* Big sun behind */}
    <div className="absolute -top-20 -right-20 opacity-40 hidden md:block">
      <SolarSun />
    </div>
    <div className="ps-container relative">
      <div className="max-w-3xl reveal">
        <Eyebrow>Order Today</Eyebrow>
        <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold mt-4 leading-[1] tracking-tight">
          Order Solar Cleaning<br /> Brush in India Today
        </h2>
        <p className="mt-5 text-black/70 text-lg max-w-xl">
          Join 5,000+ Indian homes powering their rooftops at 100% efficiency with PanelShine. Same-day dispatch, WhatsApp support, free replacement.
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <WABtn label="WhatsApp Now" size="lg" testId="final-wa-btn" />
          <a href={`tel:${PHONE_TEL}`} className="btn-dark" data-testid="final-call-btn">
            <Phone className="w-4 h-4" />
            <span className="sm:hidden">Call Now</span>
            <span className="hidden sm:inline">Call Now · {PHONE_DISPLAY}</span>
          </a>
        </div>
      </div>
    </div>
  </section>
);

const Footer = () => (
  <footer className="bg-black text-white py-14" data-testid="footer">
    <div className="ps-container grid md:grid-cols-4 gap-8">
      <div className="md:col-span-2">
        <div className="flex items-center gap-2.5">
          <Logo size={42} />
          <span className="font-display font-bold text-xl">PanelShine</span>
        </div>
        <p className="mt-4 text-white/60 text-sm max-w-sm">
          India's trusted solar panel cleaning brand. Engineered for rooftops in Indian conditions — dust, pollution, droppings, sunshine.
        </p>
        <div className="mt-5 text-sm text-white/60 space-y-1.5">
          <div>Surat, Gujarat, India</div>
          <a href={`tel:${PHONE_TEL}`} className="block hover:text-white" data-testid="footer-phone">{PHONE_DISPLAY}</a>
          <a href={`mailto:${EMAIL}`} className="block hover:text-white" data-testid="footer-email">{EMAIL}</a>
        </div>
      </div>
      <div>
        <div className="font-display font-bold mb-4">Shop</div>
        <ul className="space-y-2 text-sm text-white/60">
          <li><a href="#product" className="hover:text-white">Cleaning Brush</a></li>
          <li><a href="#product" className="hover:text-white">Cleaning Liquid</a></li>
          <li><a href="#sizes" className="hover:text-white">Size Guide</a></li>
          <li><a href="#wholesale" className="hover:text-white text-[color:var(--ps-yellow)]">Wholesale / Bulk</a></li>
        </ul>
      </div>
      <div>
        <div className="font-display font-bold mb-4">Support</div>
        <ul className="space-y-2 text-sm text-white/60">
          <li><a href="#faq" className="hover:text-white">FAQ</a></li>
          <li><a href={buildWA()} target="_blank" rel="noreferrer" className="hover:text-white">WhatsApp</a></li>
          <li><a href={`tel:${PHONE_TEL}`} className="hover:text-white">Call</a></li>
          <li><a href={`mailto:${EMAIL}`} className="hover:text-white">Email</a></li>
        </ul>
      </div>
    </div>
    <div className="ps-container mt-10 pt-6 border-t border-white/10 flex flex-wrap justify-between gap-4 text-xs text-white/40">
      <div>© {new Date().getFullYear()} PanelShine. All rights reserved.</div>
      <div>Made in India · For Indian Rooftops</div>
    </div>
  </footer>
);

const FloatingWA = () => (
  <a
    href={buildWA()}
    target="_blank"
    rel="noopener noreferrer"
    className="wa-float fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-[#25D366] text-white flex items-center justify-center shadow-2xl hover:scale-110 transition-transform"
    aria-label="Order on WhatsApp"
    data-testid="floating-wa-btn"
  >
    <WhatsAppIcon className="w-7 h-7" />
  </a>
);

/* Wholesale / Bulk Inquiry section */
const Wholesale = () => {
  const [qty, setQty] = useState(50);
  const [role, setRole] = useState("Solar Installer");
  const roles = ["Solar Installer", "Distributor / Reseller", "Housing Society", "Industrial / Commercial", "Other"];
  const msg = `Hi, I'd like to make a WHOLESALE / BULK inquiry for PanelShine. Role: ${role}. Quantity: ${qty} units. Please share bulk pricing and dealer terms.`;
  return (
    <section id="wholesale" className="py-20 md:py-28 bg-[color:var(--ps-yellow)] relative overflow-hidden" data-testid="wholesale-section">
      <div className="absolute inset-0 grain" />
      <div className="absolute -top-32 -right-24 w-[420px] h-[420px] rounded-full bg-white/30 blur-3xl" />
      <div className="ps-container relative z-10 grid md:grid-cols-12 gap-10 items-start">
        <div className="md:col-span-6 reveal">
          <Eyebrow>Wholesale · Bulk · B2B</Eyebrow>
          <h2 className="font-display text-4xl sm:text-5xl font-bold mt-4 leading-tight">
            Become a PanelShine<br /> dealer or distributor
          </h2>
          <p className="mt-4 text-black/75 max-w-md text-base">
            Running a solar installation business? Own a housing society? Buying for a commercial/industrial site? Get bulk pricing, co-branded packaging options, and priority dispatch across India.
          </p>

          <div className="mt-8 grid grid-cols-3 gap-4">
            {[
              { n: "25%+", l: "Bulk discount" },
              { n: "48hr", l: "Priority dispatch" },
              { n: "All India", l: "Distribution" },
            ].map((b, i) => (
              <div key={i} className="bg-black/90 rounded-2xl p-4 text-white">
                <div className="font-display font-bold text-2xl text-[color:var(--ps-yellow)]">{b.n}</div>
                <div className="text-[11px] uppercase tracking-widest mt-1 opacity-70">{b.l}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Inquiry card */}
        <div className="md:col-span-6 reveal">
          <div className="bg-black text-white rounded-3xl p-7 md:p-9 shadow-2xl">
            <div className="flex items-center gap-2 text-xs font-bold tracking-widest uppercase text-[color:var(--ps-yellow)]">
              <span className="pulse-dot" style={{ background: "#FFC107" }} /> Bulk Inquiry
            </div>
            <h3 className="font-display text-2xl font-bold mt-3">Tell us your requirement</h3>

            {/* Role selector */}
            <div className="mt-6">
              <div className="text-[11px] font-bold uppercase tracking-widest text-white/60 mb-2">I am a</div>
              <div className="flex flex-wrap gap-2">
                {roles.map((r) => (
                  <button
                    key={r}
                    type="button"
                    onClick={() => setRole(r)}
                    className={`text-xs font-semibold px-3 py-2 rounded-full border transition ${
                      role === r
                        ? "bg-[color:var(--ps-yellow)] text-black border-[color:var(--ps-yellow)]"
                        : "bg-white/5 text-white/80 border-white/15 hover:border-white/40"
                    }`}
                    data-testid={`wholesale-role-${r.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
                  >
                    {r}
                  </button>
                ))}
              </div>
            </div>

            {/* Quantity slider */}
            <div className="mt-7">
              <div className="flex items-center justify-between mb-3">
                <div className="text-[11px] font-bold uppercase tracking-widest text-white/60">Estimated quantity</div>
                <div className="font-display font-bold text-2xl text-[color:var(--ps-yellow)]" data-testid="wholesale-qty-value">{qty} <span className="text-xs text-white/60 font-sans font-medium tracking-wide">units</span></div>
              </div>
              <input
                type="range"
                min="10"
                max="500"
                step="10"
                value={qty}
                onChange={(e) => setQty(Number(e.target.value))}
                className="ws-range w-full"
                style={{ "--val": `${((qty - 10) / 490) * 100}%` }}
                data-testid="wholesale-qty-slider"
              />
              <div className="flex justify-between text-[10px] text-white/40 tracking-widest mt-2">
                <span>10</span><span>100</span><span>250</span><span>500+</span>
              </div>
            </div>

            <WABtn msg={msg} label="Send Inquiry on WhatsApp" size="lg" className="mt-8 w-full" testId="wholesale-wa-btn" />
            <div className="mt-3 grid grid-cols-2 gap-2">
              <a href={`tel:${PHONE_TEL}`} className="btn-outline-light" data-testid="wholesale-call-btn">
                <Phone className="w-4 h-4" /> Call
              </a>
              <a href={`mailto:${EMAIL}?subject=PanelShine%20Wholesale%20Inquiry&body=${encodeURIComponent(msg)}`} className="btn-outline-light" data-testid="wholesale-email-btn">
                <MessageCircle className="w-4 h-4" /> Email
              </a>
            </div>

            <div className="mt-5 text-[11px] text-white/50 text-center">
              {PHONE_DISPLAY} · {EMAIL} · Reply within 2 working hours
            </div>
          </div>
        </div>
      </div>
      <EnergyDivider />
    </section>
  );
};
const VideoDemo = ({ onOpen }) => (
  <section id="watch-demo" className="py-20 md:py-28 bg-black text-white relative overflow-hidden" data-testid="video-demo-section">
    {/* subtle aurora for the dark section */}
    <div className="absolute -top-20 left-1/2 -translate-x-1/2 w-[800px] h-[400px] rounded-full blur-3xl opacity-25 pointer-events-none"
      style={{ background: "radial-gradient(circle, #FFC107, transparent 70%)" }} />

    <div className="ps-container relative z-10">
      <div className="grid md:grid-cols-12 gap-10 items-center">
        <div className="md:col-span-5 reveal">
          <Eyebrow light>Watch Demo</Eyebrow>
          <h2 className="font-display text-4xl sm:text-5xl font-bold mt-4 leading-tight">
            See PanelShine<br /> in action
          </h2>
          <p className="mt-4 text-white/70 max-w-md">
            Watch our 2-minute video demo: how to attach the brush, extend the telescopic pole, and clean an entire rooftop in under 10 minutes — from the ground, without a ladder.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <button type="button" onClick={onOpen} className="btn-primary" data-testid="demo-watch-btn">
              <Play className="w-4 h-4 fill-current" /> Play Video
            </button>
            <WABtn label="Order After Watching" testId="demo-wa-btn" />
          </div>

          <div className="mt-10 grid grid-cols-3 gap-4 text-xs">
            <div>
              <div className="font-display font-bold text-2xl text-[color:var(--ps-yellow)]">10 min</div>
              <div className="text-white/50 mt-1 tracking-wide">Full rooftop</div>
            </div>
            <div>
              <div className="font-display font-bold text-2xl text-[color:var(--ps-yellow)]">0</div>
              <div className="text-white/50 mt-1 tracking-wide">Ladder required</div>
            </div>
            <div>
              <div className="font-display font-bold text-2xl text-[color:var(--ps-yellow)]">+30%</div>
              <div className="text-white/50 mt-1 tracking-wide">Output gain</div>
            </div>
          </div>
        </div>

        <div className="md:col-span-7 reveal">
          <button
            type="button"
            onClick={onOpen}
            className="video-thumb group"
            aria-label="Play PanelShine demo video"
            data-testid="demo-thumbnail-btn"
          >
            <img
              src={YT_THUMB}
              alt="PanelShine solar panel cleaning brush demo video thumbnail"
              loading="lazy"
              onError={(e) => {
                e.currentTarget.src = `https://img.youtube.com/vi/${YT_ID}/hqdefault.jpg`;
              }}
            />
            {/* gradient overlay */}
            <div className="video-thumb-overlay" />
            {/* play button */}
            <span className="video-play-btn">
              <span className="video-play-pulse" />
              <span className="video-play-pulse video-play-pulse-2" />
              <Play className="w-8 h-8 fill-black text-black translate-x-0.5" />
            </span>
            {/* Bottom info strip */}
            <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between text-white">
              <div className="text-xs tracking-widest uppercase font-bold">Demo · 2 min</div>
              <div className="text-xs font-semibold bg-white/10 backdrop-blur border border-white/20 rounded-full px-3 py-1.5">
                HD · YouTube
              </div>
            </div>
          </button>
        </div>
      </div>
    </div>
    <EnergyDivider />
  </section>
);

/* ----------------------------- App ----------------------------- */
function App() {
  useReveal();
  const [videoOpen, setVideoOpen] = useState(false);
  const openVideo = () => setVideoOpen(true);
  const closeVideo = () => setVideoOpen(false);

  return (
    <div className="App" data-testid="app-root">
      <OfferBar />
      <Nav />
      <main>
        <Hero onWatchDemo={openVideo} />
        <Problem />
        <Solution />
        <VideoDemo onOpen={openVideo} />
        <ProductMain />
        <ProductLiquid />
        <Compare />
        <SizeGuide />
        <Benefits />
        <HowItWorks />
        <Testimonials />
        <Wholesale />
        <Policy />
        <FAQSection />
        <FinalCTA />
      </main>
      <Footer />
      <FloatingWA />
      <VideoModal open={videoOpen} onClose={closeVideo} />
    </div>
  );
}

export default App;
